"use client";

import { useCallback, useEffect, useRef } from "react";

interface UseThreadsResyncOpts {
  enabled: boolean;
  /** Refetch the thread list. Reject (or throw) on failure so the live overlay is kept. */
  refetch: () => Promise<unknown>;
  /** The refetch succeeded; `startedAt` is when it was REQUESTED (client clock). Live content
   *  older than that is superseded by the result (see `pruneThreadLive`). */
  onSettled: (startedAt: number) => void;
}

/**
 * Brings the thread list back in line with the server after a window in which live frames may
 * have been lost: the stream reconnected, the network came back, or the tab became visible again.
 * Returns the `resync` function so the shell can also call it for its own triggers (a
 * `threadAdded` discovery, the Retry button). Concurrent triggers collapse into one request.
 */
export function useThreadsResync({
  enabled,
  refetch,
  onSettled,
}: UseThreadsResyncOpts): () => Promise<void> {
  const enabledRef = useRef(enabled);
  const refetchRef = useRef(refetch);
  const onSettledRef = useRef(onSettled);
  const inFlight = useRef(false);
  useEffect(() => {
    enabledRef.current = enabled;
    refetchRef.current = refetch;
    onSettledRef.current = onSettled;
  });

  const resync = useCallback(async () => {
    if (!enabledRef.current || inFlight.current) return;
    inFlight.current = true;
    const startedAt = Date.now();
    try {
      await refetchRef.current();
      onSettledRef.current(startedAt);
    } catch {
      // Still offline / BE down: keep what we have; the next trigger retries.
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const onOnline = () => void resync();
    const onVisible = () => {
      if (!document.hidden) void resync();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, resync]);

  return resync;
}
